import type { FC } from "react"
import React, { useEffect, useRef } from "react"
import { Box, Flex } from "@chakra-ui/react"

import { useAddress } from "web3/hooks/useAddress"
import CreateProposalForm from "createProposal/components/CreateProposalForm"
import { useRouter } from "common/hooks/useRouter"
import { FeatureFlag, useGovernanceContext } from "governance/context"
import { ROUTES } from "common/constants/routes"
import { useMe } from "user/providers/MeProvider"
import type { EditTallyProposalQuery } from "query/graphql"

const CreateProposal: FC<{
  isEditMode?: boolean
  isWhiteLabel: boolean
  governanceSlug: string
  tallyProposal?: EditTallyProposalQuery["proposal"]
  organizationId: string
  originalProposalId?: string
}> = ({
  isWhiteLabel,
  governanceSlug,
  tallyProposal,
  organizationId,
  originalProposalId,
  isEditMode = false,
}) => {
  const firstUpdate = useRef(true)
  const { push } = useRouter()
  const walletAddress = useAddress()
  const me = useMe()
  const { isFeatureFlagOn, governor } = useGovernanceContext()

  const slug = governor?.slug || ""

  const hideCreateProposal = isFeatureFlagOn(FeatureFlag.HideCreateProposal)

  useEffect(() => {
    // Sometimes the me object is undefined on the first useEffect
    if (firstUpdate.current) {
      firstUpdate.current = false

      return
    }

    // If user disconnects wallet or sign out, redirect to user connect page
    if (!walletAddress || !me) {
      push(
        ROUTES.user.connect(ROUTES.governance.proposal.create(governanceSlug)),
      )
    }
  }, [governanceSlug, me, push, walletAddress])

  useEffect(() => {
    // If hide create proposal flag is enabled, then redirect to Governance page
    if (hideCreateProposal) {
      push(ROUTES.governance.id(slug))
    }
  }, [hideCreateProposal, slug, push])

  return (
    <Flex justify="center">
      <Box width="full">
        <CreateProposalForm
          governanceSlug={governanceSlug}
          isEditMode={isEditMode}
          isWhiteLabel={isWhiteLabel}
          organizationId={organizationId}
          originalProposalId={originalProposalId}
          tallyProposal={tallyProposal}
        />
      </Box>
    </Flex>
  )
}

export default CreateProposal
